import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "./AlphabetScrubberRail.css";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function AlphabetScrubberRail({
  activeLetter,
  onSelectLetter,
  visible = true,
}) {
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [currentLetter, setCurrentLetter] = useState(activeLetter || "A");
  const [badgeY, setBadgeY] = useState(0);

  const railRef = useRef(null);

  // Process gesture/touch along the fixed rail
  const handleGesture = useCallback((clientY) => {
    if (!railRef.current) return;
    const rect = railRef.current.getBoundingClientRect();
    const relativeY = Math.max(0, Math.min(clientY - rect.top, rect.height));
    const ratio = relativeY / rect.height;

    setBadgeY(relativeY);

    const letterIndex = Math.min(
      ALPHABET.length - 1,
      Math.floor(ratio * ALPHABET.length)
    );
    const letter = ALPHABET[letterIndex];

    setCurrentLetter(letter);
    if (onSelectLetter) {
      onSelectLetter(letter);
    }
  }, [onSelectLetter]);

  const handlePointerDown = (e) => {
    e.preventDefault();
    setIsScrubbing(true);
    handleGesture(e.clientY);
  };

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (isScrubbing) {
        e.preventDefault();
        handleGesture(e.clientY);
      }
    };

    const handlePointerUp = () => {
      setIsScrubbing(false);
    };

    if (isScrubbing) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isScrubbing, handleGesture]);

  useEffect(() => {
    if (activeLetter) {
      setCurrentLetter(activeLetter);
    }
  }, [activeLetter]);

  if (!visible) return null;

  // Mount directly to document.body to break out of parent overflow containers
  return createPortal(
    <aside className="asr-portal-rail-wrapper">
      <div
        className={`asr-rail ${isScrubbing ? "active" : ""}`}
        ref={railRef}
        onPointerDown={handlePointerDown}
      >
        {ALPHABET.map((char) => (
          <span
            key={char}
            className={`asr-char ${currentLetter === char ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentLetter(char);
              if (onSelectLetter) onSelectLetter(char);
            }}
          >
            {char}
          </span>
        ))}

        {/* Dynamic Floating Letter Preview Badge */}
        <div
          className={`asr-badge ${isScrubbing ? "visible" : ""}`}
          style={{ top: `${badgeY}px` }}
        >
          {currentLetter}
        </div>
      </div>
    </aside>,
    document.body
  );
}